'use client'

import { cn } from '@/lib/utils'
import { useStoreContact, useStoreI18n } from '@/store'
import useStoreLoading from '@/store/store-loading'
import { Typo3SectionSpotSanityEventList } from '@/types'
import { Typo3EventItem } from '@/types/typo3/sections/spotsanity-eventlist'
import Button from '@/ui/components/Button/Button'
import { PROJECT } from '@spot'
import { usePathname } from 'next/navigation'
import { memo, useEffect, useMemo, useRef, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data: Typo3SectionSpotSanityEventList
  store: any
}

// eslint-disable-next-line
const SpotSanityPdf = ({ data, store, ...props }: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  const { storeFilteredItemsData, startDate, endDate } = store()
  const { contact } = useStoreContact()
  const { setIsLoading } = useStoreLoading()
  const D = useStoreI18n.use.dictionary()
  const locale = useStoreI18n.use.currentLocale()

  const pdfTitle = 'Events'

  const [groupedEvents, setGroupedEvents] = useState<Record<string, any>>({})
  const [isCreatePdf, setIsCreatePdf] = useState(false)

  // Memoized grouped events
  const memoizedGroupedEvents = useMemo(
    () => groupEventsByDate(storeFilteredItemsData, startDate, endDate),
    [storeFilteredItemsData, startDate, endDate]
  )

  useEffect(() => {
    setGroupedEvents(memoizedGroupedEvents)
  }, [memoizedGroupedEvents])

  const _pathname = usePathname()
  const pathname =
    process?.env?.NEXT_PUBLIC_SITE_URL?.replace(/^https?:\/\//, '') + _pathname

  const logo = new Image()
  logo.src = `/public/logo/${PROJECT}.png`
  let logoWidth = 0
  let logoHeight = 0

  if (PROJECT === 'surselva') {
    logoWidth = 29.17
    logoHeight = 8.2
  }

  // Function to group events by date
  function groupEventsByDate(
    events: Typo3EventItem[],
    filterStartDate: string | null = null,
    filterEndDate: string | null = null
  ) {
    const grouped: any = []

    const filterStart = filterStartDate
      ? new Date(filterStartDate)
      : new Date(new Date().setHours(0, 0, 1, 0))
    const filterEnd = filterEndDate ? new Date(filterEndDate) : null

    events.forEach((event: Typo3EventItem) => {
      event?.cleanDates?.forEach((date: any) => {
        const eventStartDate = new Date(date?.startDate)
        const eventEndDate = new Date(date?.endDate)

        const loopStartDate =
          filterStart > eventStartDate ? filterStart : eventStartDate
        const loopEndDate =
          filterEnd && filterEnd < eventEndDate
            ? new Date(filterEnd.setHours(23, 59, 59, 999))
            : eventEndDate

        for (
          let d = new Date(loopStartDate);
          d <= loopEndDate;
          d.setDate(d.getDate() + 1)
        ) {
          const year = d.getFullYear()
          const month = (d.getMonth() + 1).toString().padStart(2, '0')
          const day = d.getDate().toString().padStart(2, '0')

          const formattedDate = `${year}-${month}-${day}`

          if (!grouped[formattedDate]) {
            grouped[formattedDate] = []
          }

          if (!grouped[formattedDate].includes(event)) {
            grouped[formattedDate].push(event)
          }
        }
      })
    })

    return grouped
  }

  const formatEventDatesForPDF = (dates = [], currentDate: string) => {
    const allDayString = D['fe.pdf.allDay']
    let formattedText = ''

    const filteredDates = dates.filter((date: any) => {
      const start = new Date(date?.startDate)
      const end = new Date(date?.endDate)
      const current = new Date(currentDate)
      return start <= current && current <= end
    })

    if (filteredDates.length === 0) {
      return ''
    }

    filteredDates.forEach((date: any) => {
      const filterStartDate = new Date(date?.startDate)
      const filterEndDate = new Date(date?.endDate)

      const formattedDate = filterStartDate.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })

      const dateRange =
        filterStartDate.toISOString().split('T')[0] ===
        filterEndDate.toISOString().split('T')[0]
          ? formattedDate
          : `${filterStartDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} - ${filterEndDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}`

      const formattedTime =
        !date.allDay && (date.startTime || date.endTime)
          ? `${date.startTime || ''}${
              date.startTime && date.endTime ? ' - ' : ''
            }${date.endTime || ''}`
          : allDayString

      formattedText += `${dateRange}, ${formattedTime}\n`
    })

    return formattedText
  }

  function formatDate(dateString: string, type: 'long' | 'short' = 'long') {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return ''
    }
    return date.toLocaleDateString(locale, {
      weekday: type === 'long' ? 'long' : 'short',
      day: type === 'long' ? 'numeric' : '2-digit',
      month: type === 'long' ? 'long' : '2-digit',
      year: 'numeric'
    })
  }

  const textFormatedDates = (() => {
    if (startDate && endDate) {
      const formattedStartDate = formatDate(startDate, 'short')
      const formattedEndDate = formatDate(endDate, 'short')
      return `${formattedStartDate} - ${formattedEndDate}`
    }

    const allDates = Object.keys(groupedEvents).map((date) => new Date(date))
    if (allDates.length > 0) {
      const earliestDate = new Date(
        Math.min(...allDates.map((date) => date.getTime()))
      )
      const latestDate = new Date(
        Math.max(...allDates.map((date) => date.getTime()))
      )
      const formattedEarliestDate = formatDate(
        earliestDate.toISOString(),
        'short'
      )
      const formattedLatestDate = formatDate(latestDate.toISOString(), 'short')
      return `${formattedEarliestDate} - ${formattedLatestDate}`
    }

    return ``
  })()

  // Generate PDF
  const createPdf = async () => {
    setIsLoading(true)

    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'p',
        compress: true
      })
      const fontInterRegularResponse = await fetch(
        '/api/fonts/fontInterRegular'
      )
      const fontInterBoldResponse = await fetch('/api/fonts/fontInterBold')

      const fontInterRegular = await fontInterRegularResponse.text()
      const fontInterBold = await fontInterBoldResponse.text()

      doc.addFileToVFS('Inter-normal.ttf', fontInterRegular)
      doc.addFont('Inter-normal.ttf', 'Inter', 'normal')
      doc.addFileToVFS('Inter-bold.ttf', fontInterBold)
      doc.addFont('Inter-bold.ttf', 'Inter', 'bold')

      if (PROJECT === 'surselva') {
        const fontSecondaryEngadinResponse = await fetch(
          '/api/fonts/fontSecondaryEngadin'
        )
        const fontSecondaryEngadin = await fontSecondaryEngadinResponse.text()
        doc.addFileToVFS('Secondary.ttf', fontSecondaryEngadin)
        doc.addFont('Secondary.ttf', 'Secondary', 'normal')
      } else {
        doc.addFileToVFS('Secondary.ttf', fontInterRegular)
        doc.addFont('Secondary.ttf', 'Secondary', 'normal')
      }

      doc.setFont('Inter')
      doc.setTextColor(26, 25, 23)
      doc.setFont('Inter', 'normal')

      doc.html(ref.current as HTMLElement, {
        callback: (doc) => {
          const _totalPages = (doc as any)?.internal?.getNumberOfPages() || 0
          addHeaderFooter(_totalPages)
          doc.save(pdfTitle + '.pdf')
          setIsLoading(false)
        },
        autoPaging: 'text',
        html2canvas: {
          allowTaint: true,
          letterRendering: true,
          logging: false
        },
        margin: [28, 10, 18, 10],
        width: 190,
        windowWidth: 794
      })
      const addHeaderFooter = (pageCount: number) => {
        for (let i = 1; i <= pageCount; i++) {
          doc.setLineWidth(0.3)
          doc.setDrawColor(26, 25, 23)
          doc.rect(
            5,
            5,
            doc.internal.pageSize.getWidth() - 10,
            doc.internal.pageSize.getHeight() - 10,
            'S'
          )
          doc.setLineWidth(0.3)
          doc.setDrawColor(26, 25, 23)
          doc.line(5, 20, doc.internal.pageSize.getWidth() - 5, 20)
          doc.setTextColor(26, 25, 23)
          if (i === 1) {
            doc.setPage(i)
            doc.setFont('Inter')
            doc.setTextColor(26, 25, 23)
            doc.setFontSize(8)
            doc.text(PROJECT.toUpperCase(), 10, 14, { charSpace: 0.5 })
            doc.text(
              pdfTitle.toUpperCase(),
              doc.internal.pageSize.getWidth() - 12,
              14,
              {
                align: 'right',
                charSpace: 0.5
              }
            )
            doc.setFontSize(16)

            doc.text(
              textFormatedDates,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() / 2 + 5,
              { align: 'center' }
            )
            doc.setFont('Secondary')
            doc.setFontSize(64)
            doc.text(
              pdfTitle,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() / 2 - 10,
              { align: 'center' }
            )
            doc.addImage(
              logo,
              'png',
              doc.internal.pageSize.getWidth() - logoWidth * 2 - 10,
              doc.internal.pageSize.getHeight() - logoHeight * 2 - 10,
              logoWidth * 2,
              logoHeight * 2
            )
          }

          if (i > 1) {
            doc.setPage(i)
            doc.setFont('Inter')
            doc.setFontSize(8)
            doc.setLineWidth(0.3)
            doc.setDrawColor(26, 25, 23)
            doc.line(
              5,
              doc.internal.pageSize.getHeight() - 14,
              doc.internal.pageSize.getWidth() - 5,
              doc.internal.pageSize.getHeight() - 14
            )

            if (textFormatedDates) {
              doc.setFont('Inter', 'bold')
              doc.text(D['fe.pdf.selectedData'], 10, 14)
              doc.setFont('Inter', 'normal')
              doc.text(textFormatedDates, 39, 14)
            }
            doc.addImage(
              logo,
              'png',
              doc.internal.pageSize.getWidth() - logoWidth - 10,
              9,
              logoWidth,
              logoHeight
            )

            doc.text(
              `${pathname} · ${contact.tel && contact.tel} · ${contact.email}`,
              10,
              doc.internal.pageSize.getHeight() - 8.5
            )

            doc.text(
              `${i} / ${pageCount}`,
              doc.internal.pageSize.getWidth() - 10,
              doc.internal.pageSize.getHeight() - 8.5,
              { align: 'right' }
            )
          }
        }
      }
      setIsCreatePdf(false)
    } catch (error) {
      console.error('Error generating PDF:', error)
      setIsLoading(false)
      setIsCreatePdf(false)
    }
  }

  useEffect(() => {
    if (!isCreatePdf) {
      return
    }
    createPdf()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePdf])

  return (
    <>
      <Button onClick={() => setIsCreatePdf(true)} variant="text" size="s">
        {D['fe.events.generatePdf']}
      </Button>
      {isCreatePdf && (
        <div className={cn('pdf_wrapper', 'fixed right-[-1000px]')}>
          <div
            className="relative h-auto w-full max-w-[210mm] font-[Inter,sans-serif] text-[12px] leading-4"
            ref={ref}
          >
            <div className="relative flex h-[270mm] w-full flex-col items-center justify-center gap-4 text-center tracking-[0.5]"></div>
            {Object.keys(groupedEvents)
              .sort()
              .map((date) => (
                <div key={date}>
                  <h2 className="font-[Secondary,sans-serif] text-[28px] leading-[34px] font-normal tracking-[0.5] text-black">
                    {formatDate(date, 'short')}
                  </h2>
                  <div>
                    <br />
                    <div className="grid w-full grid-cols-[calc(31.25%-24px)_25%_25%_18.75%] gap-2 border-t border-t-black font-bold text-black">
                      <div>
                        <br />
                        <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                          {D['fe.pdf.event'] as any}
                        </p>
                        <br />
                      </div>
                      <div>
                        <br />
                        <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                          {D['fe.pdf.date'] as any}
                        </p>
                        <br />
                      </div>
                      <div>
                        <br />
                        <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                          {D['fe.pdf.location'] as any}
                        </p>
                        <br />
                      </div>
                      <div>
                        <br />
                        <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                          {D['fe.pdf.contact'] as any}
                        </p>
                        <br />
                      </div>
                    </div>

                    {groupedEvents?.[date]?.map(
                      (item: Typo3EventItem, index: number) => (
                        <div
                          key={date + '_' + index?.toString()}
                          className="grid w-full grid-cols-[calc(31.25%-24px)_25%_25%_18.75%] gap-2 border-t border-t-black text-black"
                        >
                          <div>
                            <br />
                            <p
                              className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word"
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 700,
                                letterSpacing: 0.2
                              }}
                            >
                              {item.title}
                            </p>
                            {item?.summary && (
                              <p
                                className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word"
                                dangerouslySetInnerHTML={{
                                  __html: item?.summary as string
                                }}
                              />
                            )}
                            <br />
                            <br />
                          </div>
                          <div>
                            <br />
                            {item?.cleanDates && (
                              <p
                                className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word"
                                dangerouslySetInnerHTML={{
                                  __html: formatEventDatesForPDF(
                                    item.cleanDates as any,
                                    date
                                  )
                                }}
                              />
                            )}
                            {item?.categories && (
                              <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                                {D['fe.events.filters.categories'] as any}:
                                &nbsp;
                                {item?.categories}
                              </p>
                            )}
                            <br />
                            <br />
                          </div>
                          <div>
                            <br />
                            {item?.infoLocation && (
                              <p className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word">
                                {item.infoLocation}
                              </p>
                            )}
                            <br />
                            <br />
                          </div>
                          <div>
                            <br />
                            <p
                              className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word"
                              dangerouslySetInnerHTML={{
                                __html: item?.contactText || ''
                              }}
                            />
                            {item?.prices && item?.prices.length ? (
                              <p
                                className="font-[Inter,sans-serif] text-[12px] leading-4 tracking-[0.5] wrap-break-word"
                                style={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 700,
                                  letterSpacing: 0.2
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: `${item?.prices?.[0]?.labelTitlePrice}
                                      <br/>
                                      ${item?.prices?.[0]?.labelPrice}
                                      `
                                }}
                              />
                            ) : (
                              ''
                            )}
                            <br />
                            <br />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  <br />
                  <br />
                  <br />
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  )
}

export default memo(SpotSanityPdf)
